import React, { useEffect, useState } from 'react';
import { getProducts, createProduct, updateProduct, deleteProduct } from '../constants/api';
import { COLORS } from '../constants/theme';

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000, padding: 0 }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 24, width: '100%', maxWidth: 540, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 -8px 40px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: COLORS.ink }}>{title}</div>
          <button onClick={onClose} style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 16, color: COLORS.inkMid }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ProductForm({ initial, onSave, onCancel, loading }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [costPrice, setCostPrice] = useState(initial?.costPrice ?? '');
  const [sellingPrice, setSellingPrice] = useState(initial?.sellingPrice ?? '');
  const [quantity, setQuantity] = useState(initial?.quantity ?? '');
  const profit = parseFloat(sellingPrice) - parseFloat(costPrice);

  const inputStyle = {
    width: '100%', padding: '13px 14px', borderRadius: 10,
    border: `1.5px solid ${COLORS.border}`, fontSize: 14,
    color: COLORS.ink, background: COLORS.bg, outline: 'none',
    fontFamily: 'inherit', boxSizing: 'border-box',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {[
        { label: 'Product Name', value: name, set: setName, type: 'text', placeholder: 'e.g. Indomie Noodles' },
        { label: 'Cost Price (₦)', value: costPrice, set: setCostPrice, type: 'number', placeholder: 'What you paid' },
        { label: 'Selling Price (₦)', value: sellingPrice, set: setSellingPrice, type: 'number', placeholder: 'What you sell for' },
        { label: 'Quantity', value: quantity, set: setQuantity, type: 'number', placeholder: 'Stock quantity' },
      ].map(field => (
        <div key={field.label}>
          <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.ink, display: 'block', marginBottom: 6 }}>{field.label}</label>
          <input type={field.type} value={field.value} onChange={e => field.set(e.target.value)} placeholder={field.placeholder} style={inputStyle}
            onFocus={e => e.target.style.borderColor = COLORS.accent}
            onBlur={e => e.target.style.borderColor = COLORS.border}
          />
        </div>
      ))}

      {!isNaN(profit) && profit > 0 && sellingPrice && costPrice && (
        <div style={{ background: COLORS.accentLight, borderRadius: 10, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.accent }}>Profit per unit</span>
          <span style={{ fontSize: 18, fontWeight: 900, color: COLORS.accent }}>₦{profit.toLocaleString()}</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: '13px', borderRadius: 10, border: `1.5px solid ${COLORS.border}`, background: 'white', fontSize: 14, fontWeight: 700, color: COLORS.inkMid, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
        <button onClick={() => onSave({ name, costPrice: parseFloat(costPrice), sellingPrice: parseFloat(sellingPrice), quantity: parseInt(quantity) })} disabled={loading}
          style={{ flex: 1, padding: '13px', borderRadius: 10, border: 'none', background: COLORS.accent, fontSize: 14, fontWeight: 800, color: 'white', cursor: 'pointer', opacity: loading ? 0.7 : 1, fontFamily: 'inherit' }}>
          {loading ? 'Saving...' : 'Save Product'}
        </button>
      </div>
    </div>
  );
}

export default function ProductsPage() {
  const isMobile = window.innerWidth < 768;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [error, setError] = useState('');

  const fetchProducts = async () => {
    try {
      const res = await getProducts();
      setProducts(res.data);
    } catch (err) {
      console.log('Error:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  const filtered = products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));

  const handleAdd = async (data) => {
    if (!data.name || isNaN(data.costPrice) || isNaN(data.sellingPrice) || isNaN(data.quantity)) return setError('Please fill in all fields correctly.');
    setSaving(true);
    try {
      await createProduct(data);
      await fetchProducts();
      setShowAdd(false);
      setError('');
    } catch (err) {
      setError(err?.response?.data ?? 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (data) => {
    setSaving(true);
    try {
      await updateProduct(editProduct.id, data);
      await fetchProducts();
      setEditProduct(null);
      setError('');
    } catch (err) {
      setError(err?.response?.data ?? 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await deleteProduct(id);
      await fetchProducts();
    } catch {
      alert('Could not delete product.');
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: COLORS.accent }}>Loading...</div>
  );

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '32px', maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: isMobile ? 20 : 26, fontWeight: 900, color: COLORS.ink }}>Products</div>
          <div style={{ fontSize: 13, color: COLORS.inkLight, marginTop: 4 }}>{products.length} products</div>
        </div>
        <button onClick={() => setShowAdd(true)} style={{ background: COLORS.accent, color: 'white', border: 'none', borderRadius: 10, padding: '11px 16px', fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>
          + Add
        </button>
      </div>

      {/* Search */}
      <input
        type="text" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)}
        style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1.5px solid ${COLORS.border}`, fontSize: 14, color: COLORS.ink, background: 'white', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 16 }}
        onFocus={e => e.target.style.borderColor = COLORS.accent}
        onBlur={e => e.target.style.borderColor = COLORS.border}
      />

      {/* Products list */}
      {filtered.length === 0 ? (
        <div style={{ background: 'white', borderRadius: 16, padding: 48, border: `1px solid ${COLORS.border}`, textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>📦</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.ink, marginBottom: 6 }}>{search ? `No results for "${search}"` : 'No products yet'}</div>
          {!search && <button onClick={() => setShowAdd(true)} style={{ background: COLORS.accent, color: 'white', border: 'none', borderRadius: 10, padding: '11px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginTop: 8 }}>+ Add Product</button>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(product => {
            const profit = product.sellingPrice - product.costPrice;
            const lowStock = product.quantity <= 5;
            return (
              <div key={product.id} style={{ background: 'white', borderRadius: 14, border: `1px solid ${COLORS.border}`, padding: '14px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.ink }}>{product.name}</div>
                    <div style={{ fontSize: 12, color: COLORS.inkLight, marginTop: 4 }}>
                      Sell: ₦{product.sellingPrice.toLocaleString()} · Cost: ₦{product.costPrice.toLocaleString()}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent }}>+₦{profit.toLocaleString()} profit</span>
                      <span style={{ fontSize: 12, color: lowStock ? COLORS.danger : COLORS.inkLight, fontWeight: lowStock ? 700 : 400 }}>
                        {product.quantity} in stock {lowStock && '⚠️'}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginLeft: 12 }}>
                    <button onClick={() => setEditProduct(product)} style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${COLORS.border}`, background: 'white', fontSize: 12, fontWeight: 700, color: COLORS.inkMid, cursor: 'pointer', fontFamily: 'inherit' }}>Edit</button>
                    <button onClick={() => handleDelete(product.id)} style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid #fca5a5`, background: '#FEE2E2', fontSize: 12, fontWeight: 700, color: COLORS.danger, cursor: 'pointer', fontFamily: 'inherit' }}>Del</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && (
        <Modal title="Add Product" onClose={() => { setShowAdd(false); setError(''); }}>
          {error && <div style={{ background: '#FEE2E2', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: COLORS.danger, fontWeight: 600, marginBottom: 16 }}>{error}</div>}
          <ProductForm onSave={handleAdd} onCancel={() => { setShowAdd(false); setError(''); }} loading={saving} />
        </Modal>
      )}

      {editProduct && (
        <Modal title="Edit Product" onClose={() => { setEditProduct(null); setError(''); }}>
          {error && <div style={{ background: '#FEE2E2', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: COLORS.danger, fontWeight: 600, marginBottom: 16 }}>{error}</div>}
          <ProductForm initial={editProduct} onSave={handleEdit} onCancel={() => { setEditProduct(null); setError(''); }} loading={saving} />
        </Modal>
      )}
    </div>
  );
}